import React, { Component } from 'react';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
    console.error('[ErrorBoundary] Caught error:', error, errorInfo);
  }

  handleReset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  /**
   * Safely extract a renderable error message string.
   * Never returns a raw object — always a primitive string.
   */
  private getErrorMessage(): string {
    const { error } = this.state;
    if (!error) return 'An unexpected error occurred.';
    if (error instanceof Error && typeof error.message === 'string') {
      return error.message || 'An unexpected error occurred.';
    }
    if (typeof error === 'string') return error;
    try {
      return String(error);
    } catch {
      return 'An unexpected error occurred.';
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const errorMessage = this.getErrorMessage();

      return (
        <div className="flex min-h-[400px] flex-col items-center justify-center px-6 py-12 text-center">
          <div className="rounded-xl border border-red-200 bg-red-50 p-8 shadow-sm dark:border-red-800 dark:bg-red-950/30">
            <svg
              className="mx-auto mb-4 h-12 w-12 text-[#EF4444]"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
              />
            </svg>
            <h2 className="mb-2 text-lg font-semibold text-[#0F172A] dark:text-[#F8FAFC]">
              Something went wrong
            </h2>
            <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
              {errorMessage}
            </p>
            <button
              onClick={this.handleReset}
              className="inline-flex items-center rounded-lg bg-[#7C3AED] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#6D28D9] focus:outline-none focus:ring-2 focus:ring-[#7C3AED] focus:ring-offset-2"
            >
              Try Again
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;